import { Component, useEffect, useRef } from 'react'

// ── Error boundary ────────────────────────────────────────────────────────────
// Catches any React render crash inside MermaidInner and shows a code fallback.

class MermaidErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { crashed: false }
  }

  static getDerivedStateFromError() {
    return { crashed: true }
  }

  componentDidCatch(err) {
    console.error('[MermaidDiagram] error boundary caught:', err)
  }

  render() {
    if (this.state.crashed) {
      return (
        <pre style={{
          padding: '1rem', background: '#f1f5f9', borderRadius: 4,
          fontSize: 12, overflowX: 'auto', textAlign: 'left', color: '#475569',
        }}>
          {this.props.code}
        </pre>
      )
    }
    return this.props.children
  }
}

// ── Counter lives outside the component so IDs stay unique across re-renders ─

let _id = 0

// ── Inner renderer — Mermaid is imported ONLY inside useEffect ────────────────

function MermaidInner({ code }) {
  const containerRef = useRef(null)

  useEffect(() => {
    let alive = true

    ;(async () => {
      if (!containerRef.current) return
      try {
        const { default: mermaid } = await import('mermaid')
        mermaid.initialize({ startOnLoad: false, theme: 'default' })
        const diagramId = `mermaid-${++_id}`
        const { svg } = await mermaid.render(diagramId, code.trim())
        if (alive && containerRef.current) {
          containerRef.current.innerHTML = svg
        }
      } catch (err) {
        console.error('[MermaidDiagram] render failed — showing raw code:', err)
        if (alive && containerRef.current) {
          containerRef.current.innerHTML =
            `<pre style="font-size:12px;text-align:left;color:#475569;overflow-x:auto">${code}</pre>`
        }
      }
    })()

    return () => { alive = false }
  }, [code])

  return (
    <div
      ref={containerRef}
      style={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    />
  )
}

// ── Public component — wrap in error boundary, show title + caption ───────────

export default function MermaidDiagram({ code, title, caption }) {
  return (
    <MermaidErrorBoundary code={code}>
      <figure style={{
        margin: '0 0 1.5rem',
        padding: '1.5rem',
        background: '#f8f6f2',
        border: '1px solid #d9d0c3',
        borderRadius: 6,
        textAlign: 'center',
      }}>
        {title && (
          <figcaption style={{
            fontWeight: 700, fontSize: '0.875rem',
            color: '#1e293b', marginBottom: '1rem',
          }}>
            {title}
          </figcaption>
        )}
        <MermaidInner code={code} />
        {caption && (
          <figcaption style={{
            fontStyle: 'italic', fontSize: '0.8rem',
            color: '#64748b', marginTop: '1rem',
          }}>
            {caption}
          </figcaption>
        )}
      </figure>
    </MermaidErrorBoundary>
  )
}
